import { Alert, Box, Card, Chip, Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { FormattedDate, FormattedNumber } from 'react-intl'
import { CardActionAreaLink } from '@/helpers/compositions/CardActionAreaLink'
import { FormattedClazz } from '@/helpers/FormattedClazz'
import { T } from '@/helpers/T'
import { EClass } from '@/modules/_misc'
import { EForeignCurrency } from '@/modules/_misc/enums/ForeignCurrency'
import { CONFIG } from '@/utils/misc'
import type { TSpotlightResponseItem } from './Spotlight'
import { getSpotlightResultLink } from './Spotlight.misc'

//	///////////////////////
//	MARK: TYPES
interface IProps {
  totalCount: number

  results: TSpotlightResponseItem[]

  hideModalAndReset: () => void
}

//	///////////////////////
//	MARK: SUB-COMPONENT
const Result: React.FC<{ value: TSpotlightResponseItem; hideModalAndReset: () => void }> = ({
  value,
  hideModalAndReset,
}) => {
  const link = useMemo(() => getSpotlightResultLink(value), [value])

  const content = useMemo(() => {
    switch (value.clazz) {
      case EClass.Expense:
      case EClass.BelgianDepartingFlightTaxesExpense: {
        return (
          <>
            <Typography fontWeight='500'>
              <T
                id='m.spotlight.fiscal'
                values={{
                  number: value.number,
                  date: <FormattedDate value={value.date} timeZone={CONFIG.timeZones.accounting} />,
                  amount: (
                    <FormattedNumber
                      value={
                        (value.selectedCurrency === EForeignCurrency.EUR
                          ? value.amount
                          : value.foreignCurrencyTotalAmount) ?? 0
                      }
                      style='currency'
                      format={value.selectedCurrency}
                    />
                  ),
                }}
              />
            </Typography>{' '}
            {value.supplier.fullName}
          </>
        )
      }
      case EClass.Invoice:
      case EClass.AircraftOwnerInvoice:
      case EClass.MissionInvoice: {
        return (
          <>
            <Typography fontWeight='500'>
              <T
                id='m.spotlight.fiscal'
                values={{
                  number: value.number,
                  date: <FormattedDate value={value.date} timeZone={CONFIG.timeZones.accounting} />,
                  amount: (
                    <FormattedNumber
                      value={
                        (value.selectedCurrency === EForeignCurrency.EUR
                          ? value.amount
                          : value.foreignCurrencyTotalAmount) ?? 0
                      }
                      style='currency'
                      format={value.selectedCurrency}
                    />
                  ),
                }}
              />
            </Typography>{' '}
            {value.customer.fullName}
          </>
        )
      }
      case EClass.Organisation:
      case EClass.Person:
      case EClass.User: {
        return <Typography fontWeight='500'>{value.fullName}</Typography>
      }
      case EClass.Membership:
      case EClass.BankAccount:
      case EClass.CreditCard:
      case EClass.Domiciliation: {
        return <Typography fontWeight='500'>{value.label}</Typography>
      }
      case EClass.CarResource:
      case EClass.SIMCardResource:
      case EClass.FuelCardResource:
      case EClass.ITResource:
      case EClass.AssetResource: {
        return <Typography fontWeight='500'>{value.label}</Typography>
      }
      default: {
        return null
      }
    }
  }, [
    value.amount,
    value.clazz,
    value.customer?.fullName,
    value.date,
    value.foreignCurrencyTotalAmount,
    value.fullName,
    value.label,
    value.number,
    value.selectedCurrency,
    value.supplier?.fullName,
  ])

  return (
    <Card variant='outlined'>
      <CardActionAreaLink to={link ?? ''} sx={{ p: 2 }} onClick={hideModalAndReset}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography>{content}</Typography>
          <Chip label={<FormattedClazz clazz={value.clazz} />} />
        </Box>
      </CardActionAreaLink>
    </Card>
  )
}

//	///////////////////////
//	MARK: EXPORTS
const SpotlightResultsInternal: React.FC<IProps> = ({ totalCount, results, hideModalAndReset }) => {
  if (results.length === 0) {
    return (
      <Alert severity='warning'>
        <T id='m.spotlight.no_results' />
      </Alert>
    )
  }

  return (
    <>
      <Typography variant='caption' component='p' sx={{ mb: 1 }}>
        <T id='m.spotlight.results' values={{ count: totalCount }} />
      </Typography>
      <Box maxHeight={400} overflow='auto'>
        <Stack spacing={1}>
          {results.map((e) => (
            <Result key={`${e.clazz}_${e.id}`} value={e} hideModalAndReset={hideModalAndReset} />
          ))}
        </Stack>
      </Box>
    </>
  )
}

export const SpotlightResults = React.memo(
  SpotlightResultsInternal,
) as typeof SpotlightResultsInternal
